'use client';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types';
import classNames from 'classnames';
import Link from 'next/link';
import { ReactNode } from 'react';
import ContentfulGeneric from '../ContentfulGeneric/ContentfulGeneric';

import './MainBodyToReact.scss';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { API_ASSET_ROUTE } from '@/config/constants';
import { handleLinkClick } from '@/utils/handleLinkClick';
import CustomImage from '@/components/CustomImage/CustomImage';

type TClasses = {
    h2?: string;
    h3?: string;
    h4?: string;
    p?: string;
    ul?: string;
    a?: string;
};

type TMainBodyToReactProps = {
    body: any;
    id?: string;
    fieldId?: string;
    config?: any;
    classes?: TClasses;
    hardLinksTarget?: '_blank' | '_self';
};

const GAMailToLinksHandler = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const url = e.currentTarget.href;

    window.dataLayer && window.dataLayer.push({ event: 'cta_click', cta_type: 'mailto' });
    window.dataLayer && window.dataLayer.push({ event: 'mailto_link_click' });

    window.location.href = url;
};

// Default rendering options
const getDefaultOptions = (classes: TClasses) => {
    const defaultClasses = {
        h2: 'tw-font-light',
        h3: 'tw-font-light',
        h4: 'tw-font-light',
        p: 'tw-text-base tw-leading-[29px]',
    };

    return {
        renderMark: {
            [MARKS.BOLD]: (text: ReactNode) => <span className="tw-font-gotham-bold">{text}</span>,
        },
        renderNode: {
            [BLOCKS.HEADING_3]: (_: any, children: ReactNode) => (
                <h3
                    className={classNames(
                        'tw-font-gotham-bold tw-text-[28px] tw-leading-[33px] tw-mb-[8px]',
                        classes?.h3 || defaultClasses.h3,
                    )}
                >
                    {children}
                </h3>
            ),
            [BLOCKS.HEADING_4]: (_: any, children: ReactNode) => (
                <h4
                    className={classNames(
                        'tw-font-gotham-medium tw-text-[14px] md:tw-text-[12px] lg:tw-text-[14px] tw-text-pink1 tw-pb-[8px] ',
                        classes?.h4 || defaultClasses.h4,
                    )}
                >
                    {children}
                </h4>
            ),
            [BLOCKS.HEADING_2]: (_: any, children: ReactNode) => (
                <h2
                    className={classNames(
                        'tw-font-gotham-bold tw-text-[#003865] tw-text-[26px] lg:tw-text-[32px] tw-leading-[36px] tw-pb-[12px]',
                        classes?.h2 || defaultClasses.h2,
                    )}
                >
                    {children}
                </h2>
            ),
            [BLOCKS.PARAGRAPH]: ({ content }: any, children: ReactNode) => {
                if (content[0].value.startsWith('<')) {
                    return (
                        <div
                            className="tw-flex tw-justify-center tw-pb-8"
                            dangerouslySetInnerHTML={{ __html: content[0].value }}
                        />
                    );
                }

                return (
                    <p
                        className={classNames(
                            'tw-pb-8 tw-whitespace-break-spaces tw-font-gotham-book',
                            classes?.p || defaultClasses.p,
                        )}
                    >
                        {children}
                    </p>
                );
            },
            [BLOCKS.UL_LIST]: (_: any, children: ReactNode) => (
                <ul className="rich-ul tw-list-disc tw-pl-10 tw-font-gotham-book tw-text-base">
                    {children}
                </ul>
            ),
            [BLOCKS.OL_LIST]: (_: any, children: ReactNode) => (
                <ol className="rich-ol tw-list-decimal tw-font-gotham-book tw-pl-10 tw-text-left tw-text-base">
                    {children}
                </ol>
            ),
            [BLOCKS.TABLE]: (_: any, children: ReactNode) => (
                <table className="rich-table">
                    <tbody>{children}</tbody>
                </table>
            ),
            [INLINES.ASSET_HYPERLINK]: ({ data }: any, children: ReactNode) => {
                return (
                    <Link
                        href={`${API_ASSET_ROUTE}${data.target.fields.file.fileName}`}
                        onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                            handleLinkClick(e, data.target.fields.file?.url)
                        }
                        className="tw-text-pink1"
                        target="_blank"
                    >
                        {children}
                    </Link>
                );
            },
            [BLOCKS.EMBEDDED_ENTRY]: (node: any) => {
                return <ContentfulGeneric mainBody={[node.data.target]} />;
            },
            [BLOCKS.EMBEDDED_ASSET]: (node: any) => {
                if (node.data.target.fields.file.contentType.indexOf('image') > -1) {
                    return (
                        <CustomImage
                            src={`https:${node.data.target.fields.file.url}?fm=webp`}
                            alt={node.data.target.fields.title}
                            width={node.data.target.fields.file.details.image.width}
                            height={node.data.target.fields.file.details.image.height}
                            className="tw-mx-auto tw-pb-8"
                        />
                    );
                }
                return (
                    <>
                        <Link
                            href={`${API_ASSET_ROUTE}${node.data.target.fields.file.fileName}`}
                            className="tw-font-gotham-book tw-text-pink1 tw-leading-[29px] tw-text-base"
                            target="_blank"
                        >
                            {node.data.target.fields.title}
                        </Link>
                        <br />
                    </>
                );
            },
        },
    };
};

const MainBodyToReact: React.FC<TMainBodyToReactProps> = ({
    body,
    config,
    id = '',
    fieldId = '',
    classes = {},
    hardLinksTarget = '_self',
}) => {
    const options = config || getDefaultOptions(classes);

    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    return (
        <div
            {...inspectorProps({
                fieldId,
            })}
        >
            {documentToReactComponents(body, {
                ...options,
                renderNode: {
                    ...options.renderNode,
                    [INLINES.HYPERLINK]: ({ data }: any, children: ReactNode) => {
                        if (data.uri.startsWith('mailto')) {
                            return (
                                <Link
                                    href={data.uri}
                                    className="tw-text-pink1"
                                    onClick={GAMailToLinksHandler}
                                >
                                    {children}
                                </Link>
                            );
                        }

                        if (data.uri.startsWith('/assets/')) {
                            return (
                                <Link
                                    href={data.uri}
                                    className="tw-text-pink1"
                                    onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                                        handleLinkClick(e, data.uri)
                                    }
                                >
                                    {children}
                                </Link>
                            );
                        }

                        return (
                            <Link
                                href={data.uri}
                                className="tw-text-pink1"
                                target={hardLinksTarget}
                            >
                                {children}
                            </Link>
                        );
                    },
                },
            })}
        </div>
    );
};

export default MainBodyToReact;
